import { athlete, contact } from "@/data/athlete";
import { SectionHeader } from "./_shared";
import { Mail, Phone, User, Instagram } from "lucide-react";

export const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-zinc-900 py-16 md:py-24"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <SectionHeader
          eyebrow="07 — Get In Touch"
          title={`Recruit ${athlete.firstName}`}
          kicker="Coaches: Contact directly for film, transcripts, or visit scheduling."
          testId="contact-header"
        />

        {/* Prominent direct contact banner */}
        <div
          data-testid="contact-direct-banner"
          className="mb-8 md:mb-10 border-2 border-[#FF3B30] bg-[#141414] p-6 md:p-8"
        >
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF3B30] mb-4">
            Direct Contact
          </div>
          <div className="grid md:grid-cols-2 gap-5 md:gap-8">
            <a
              data-testid="contact-direct-email"
              href={`mailto:${contact.email}`}
              className="group flex items-center gap-4 text-white hover:text-[#FF3B30] transition-colors"
            >
              <span className="h-12 w-12 shrink-0 flex items-center justify-center bg-[#FF3B30] text-white group-hover:bg-[#FF5147] transition-colors">
                <Mail className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                  Email
                </div>
                <div className="font-display text-xl md:text-2xl uppercase tracking-tight break-all">
                  {contact.email}
                </div>
              </div>
            </a>
            <a
              data-testid="contact-direct-phone"
              href={`tel:${contact.phone.replace(/[^0-9]/g, "")}`}
              className="group flex items-center gap-4 text-white hover:text-[#FF3B30] transition-colors"
            >
              <span className="h-12 w-12 shrink-0 flex items-center justify-center bg-[#FF3B30] text-white group-hover:bg-[#FF5147] transition-colors">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                  Phone
                </div>
                <div className="font-display text-xl md:text-2xl uppercase tracking-tight">
                  {contact.phone}
                </div>
              </div>
            </a>
          </div>

          {contact.instagram && (
            <a
              data-testid="contact-direct-instagram"
              href={`https://instagram.com/${contact.instagram}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-5 md:mt-8 pt-5 md:pt-6 border-t border-zinc-800 flex items-center gap-4 text-white hover:text-[#FF3B30] transition-colors"
            >
              <span className="h-12 w-12 shrink-0 flex items-center justify-center bg-[#FF3B30] text-white group-hover:bg-[#FF5147] transition-colors">
                <Instagram className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                  Instagram
                </div>
                <div className="font-display text-xl md:text-2xl uppercase tracking-tight break-all">
                  @{contact.instagram}
                </div>
              </div>
            </a>
          )}
        </div>
        <div
          data-testid="contact-guardian-card"
          className="border border-zinc-800 bg-[#141414] p-6 md:p-8 mb-6 md:mb-8"
        >
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-zinc-500 mb-4">
            Guardian
          </div>
          <div className="grid md:grid-cols-3 gap-5 md:gap-8">
            <div className="flex items-center gap-3 text-white">
              <User className="h-5 w-5 text-[#FF3B30] shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                  Name
                </div>
                <div className="text-sm md:text-base font-semibold">
                  {contact.guardianName}
                </div>
              </div>
            </div>
            {contact.guardianEmail && (
              <a
                data-testid="contact-guardian-email"
                href={`mailto:${contact.guardianEmail}`}
                className="flex items-center gap-3 text-white hover:text-[#FF3B30] transition-colors"
              >
                <Mail className="h-5 w-5 text-[#FF3B30] shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                    Email
                  </div>
                  <div className="text-sm md:text-base font-semibold break-all">
                    {contact.guardianEmail}
                  </div>
                </div>
              </a>
            )}
            <a
              data-testid="contact-guardian-phone"
              href={`tel:${contact.guardianPhone.replace(/[^0-9]/g, "")}`}
              className="flex items-center gap-3 text-white hover:text-[#FF3B30] transition-colors"
            >
              <Phone className="h-5 w-5 text-[#FF3B30] shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                  Phone
                </div>
                <div className="text-sm md:text-base font-semibold">
                  {contact.guardianPhone}
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Supporting text */}
        <p
          data-testid="contact-supporting-text"
          className="text-sm md:text-base text-zinc-400 italic"
        >
          Prefer to send details in writing? Please use the contact information
          above to get in touch.
        </p>
      </div>
    </section>
  );
};
